import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import Grid2 from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { SearchTextField } from "@/controls/SearchTextField";
import { CatalogCustomer } from "@/shared/models/CatalogCustomer";
import { useCatalogCustomers } from "../CatalogCustomersPage";
import { useTableData } from "../hooks/useTableData";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

export const TableCustomers = () => {
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [selectedRowData, setSelectedRowData] =
		useState<CatalogCustomer | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");
	const [quickFilterText, setQuickFilterText] = useState("");

	const { data: rows, isLoading } = useCatalogCustomers({
		onError: (error) => setError(error.message),
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs, getMenuOptions } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowDoubleClicked = (event: any) => {
		setSelectedRowData(event.data);
		setShowModal("update");
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "customersContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "customersContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2
					container
					size={{ md: 12, xs: 12 }}
					spacing={1}
					direction={"column"}
				>
					<Grid2>
						<Typography variant="h5" fontWeight={600}>
							Customers
						</Typography>
					</Grid2>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 size={{ md: 12, xs: 12 }} style={{ marginBottom: 10 }}>
						<SearchTextField
							value={quickFilterText}
							onChange={(text) => setQuickFilterText(text)}
							label="Search customers"
						/>
					</Grid2>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper elevation={2} style={{ height: "68vh", width: "100%" }}>
							<AgGridReact<CatalogCustomer>
								loading={isLoading}
								rowData={rows || []}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={30}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								onGridReady={(params) => setGridApi(params.api)}
								loadingOverlayComponent={CenteredLazyLoading}
								getRowId={(params) => `${params.data.CustomerId}`}
								quickFilterText={quickFilterText}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
								}}
							/>
						</Paper>
					</Grid2>
					<FooterButton
						label="CUSTOMER"
						showCreateModal={showModal === "create"}
						setShowCreateModal={(showCreateModal) =>
							showCreateModal ? setShowModal("create") : setShowModal("")
						}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<UpsertDeleteModal
				customer={selectedRowData}
				modal={showModal}
				onHide={() => {
					setShowModal("");
				}}
			/>
		</>
	);
};
